/**
 * 01 引导页 `/guide` —— **公开页，不带身份凭证**
 * -----------------------------------------------------------------------------
 * 定位（`01-报名链路.md`）：未关注者扫码后的落地页；转发链接也止步于此。
 * 因此本页：
 *   - **不请求任何需要会话的接口**（只调 `/api/h5/entrances`，它对未授权只下发公开入口）
 *   - 不读 Cookie、不展示任何个人信息
 *   - 已授权用户进来则直接进服务台（避免多一次点击）
 *
 * 版面按设计稿「学员端 · 引导页」：渐变印章 → 标题 → 关注说明卡（含二维码）→
 * 三步卡 → 琥珀提示。**本页不设任何「进入报名」按钮**。
 *
 * 为什么没有按钮（2026-10-25 拍板，推翻 T-27 A5 的「增补主按钮」结论）：
 * 报名入口**只能**从公众号菜单进入。菜单跳转带入口码，`RequireSession` 会直接
 * 静默授权，正常学员全程不落本页。会落到本页的只有两种人 ——
 *   ① 转发了链接 / 直接输地址进来的（口径 C-01 本就不该拿到业务数据）
 *   ② 扫了码但还没关注公众号的
 * 这两种人该做的动作**是同一个**：去关注，再从菜单进。页面上放一个「进入报名」
 * 反而暗示「不关注也能报名」，与第 10 行的定位自相矛盾。
 *
 * 代价（刻意接受）：授权失败（`AUTH-003/004`）被服务端 302 回本页后，没有原地
 * 重试的动作，学员只能返回公众号重发一次菜单。文案里已明确写出这一步 ——
 * 看不见按钮不等于看得见出路，提示语必须替按钮说话。
 *
 * 二维码：默认取 `public/mp-qrcode.jpg`（真实服务号码，随构建产物发布），
 * 可用 `VITE_MP_QRCODE_URL` 覆盖。图片**加载失败**时自动降级为「搜索关注」文字引导 ——
 * 空白框等于把入口第一步变成死路。系统只登记了客服码，拿客服码顶替是语义错误，
 * 所以宁可用文字动作，也不复用客服码。
 *
 * 任务卡：T-18（学员端页面）/ T-27（公众号真实接入）
 */
import { ERROR_MESSAGE_SUGGESTION } from '@enroll/shared';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { resolveEntryCode } from '../api/client';
import { getAuthorizeUrl } from '../api/h5';
import { useEntrances, useEntryCapture, useSession } from '../api/hooks';
import { ShieldIcon } from '../components/icons';
import { ListRow, Notice, PageShell } from '../components/layout';

/**
 * 公众号二维码图片。
 *
 * 默认取 `public/mp-qrcode.jpg` —— 它是真实服务号码（`gh_fae458c3657a`，344×344），
 * 由 Vite 原样复制到 `dist` 根目录，随静态资源一起发布。
 * `VITE_MP_QRCODE_URL` 可覆盖，用于换号 / 换码时不动代码。
 *
 * ⚠️ 这里**不用** `?? ''` 兜底：真实二维码已是一期交付物，若默认为空，
 * 任何一个漏配环境变量的构建都会静默退回文字引导 —— 页面看起来正常，
 * 学员却扫不到码。宁可构建时就有确定的一张码。
 *
 * 用 `?.trim() ||` 而不是 `??`：`.env.example` 里 `VITE_MP_QRCODE_URL=` 是**空字符串**，
 * `??` 只挡 `null/undefined`，照抄示例文件的人会得到空 URL、二维码静默消失。
 *
 * 用 `BASE_URL` 而不是写死 `/`：h5 若将来改挂子路径（如 `/h5/`），
 * 写死的绝对路径会 404 且很难发现。
 */
const QRCODE_URL: string =
  import.meta.env.VITE_MP_QRCODE_URL?.trim() || `${import.meta.env.BASE_URL}mp-qrcode.jpg`;

/** 关注三步（有真实二维码时，第一步是长按识别） */
const STEPS_WITH_QR: readonly string[] = [
  '长按识别上方二维码',
  '点击关注，进入公众号会话',
  '从底部菜单「学员服务」进入报名',
];

/** 图片加载失败时的降级文案：给出可执行动作，而不是让人对着空白框发愣 */
const STEPS_BY_SEARCH: readonly string[] = [
  '在微信内搜索并关注本机构公众号',
  '进入公众号会话',
  '从底部菜单「学员服务」进入报名',
];

/**
 * 授权失败码 → 学员能直接照做的动作。
 *
 * 服务端在回调失败时 302 回本页并带 `?authError=<码>`（不再吐裸 JSON）。
 * 本页没有重试按钮，所以每条提示都必须**指向同一个出口**：返回公众号重点菜单。
 * 三种失败的差别只在前半句（要不要等一等）：
 *   - 链接失效 / 超时 → 立刻可以重来，重点一次菜单就行
 *   - 微信侧不可用   → 等一会儿再点，现在点也是白点
 */
const RETRY_HINT = '请返回公众号，重新点击底部菜单「学员服务」进入';

const AUTH_ERROR_HINT: Readonly<Record<string, string>> = {
  'AUTH-003': `授权链接已失效。${RETRY_HINT}`,
  'AUTH-004': `授权已超时。${RETRY_HINT}`,
  'AUTH-005': '微信授权服务暂时不可用，请稍等几分钟再点菜单进入',
  'SYS-001': `进入时缺少必要参数。${RETRY_HINT}`,
  'SYS-004': `进入时出了点问题。${RETRY_HINT}`,
};

function authErrorText(code: string): string {
  return AUTH_ERROR_HINT[code] ?? ERROR_MESSAGE_SUGGESTION[code] ?? `进入失败。${RETRY_HINT}`;
}

/**
 * 真实公众号二维码。加载失败时回调 `onBroken` 触发降级 ——
 * 图片 404 时若仍渲染成一个空框，学员会以为「二维码没加载出来」而卡住。
 */
function QrArtwork({ onBroken }: { onBroken: () => void }) {
  return (
    <img
      className="guide-qr__image"
      src={QRCODE_URL}
      alt="机构公众号二维码，长按识别关注"
      onError={onBroken}
    />
  );
}

export default function Guide() {
  useEntryCapture();
  const navigate = useNavigate();
  const session = useSession();
  const entrances = useEntrances();
  const authorized = session.isSuccess;

  const [searchParams] = useSearchParams();
  /** 回调失败回带的错误码（服务端 302 到这里，见 `H5OAuthCallbackFilter`） */
  const authError = searchParams.get('authError');

  /** 当前生效的授权适配器：`mock` 说明没配微信凭据，联调时别误判成真机打通 */
  const [authMode, setAuthMode] = useState<'wechat' | 'mock' | null>(null);
  /** 二维码是否加载失败（404 / 图片损坏）→ 降级为「搜索关注」文字引导 */
  const [qrBroken, setQrBroken] = useState(false);
  const showQr = Boolean(QRCODE_URL) && !qrBroken;
  const handleQrBroken = useCallback(() => setQrBroken(true), []);

  /**
   * 入口码：优先取当前 URL 的 `?entry=`（OAuth 回跳后由服务端回带），
   * 其次取会话存储（更早一次落地时记住的）。
   */
  const currentEntry = searchParams.get('entry') ?? resolveEntryCode();

  useEffect(() => {
    if (authorized) navigate('/home', { replace: true });
  }, [authorized, navigate]);

  /**
   * 预先问一次模式（公开接口、无副作用），用于在页面上打出「测试模式」提示。
   * 失败静默：这只是个提示，取不到就不打，不该在本页抛错误。
   */
  useEffect(() => {
    let alive = true;
    getAuthorizeUrl(currentEntry)
      .then((result) => {
        if (alive) setAuthMode(result.mode);
      })
      .catch(() => {
        /* 静默：模式提示属辅助信息，取不到不影响「扫码关注」这条主路径 */
      });
    return () => {
      alive = false;
    };
  }, [currentEntry]);

  // 未授权时服务端只下发 `requiresAuth === false` 的公开入口
  const publicEntrances = (entrances.data?.items ?? []).filter((item) => !item.requiresAuth);

  return (
    <PageShell layout="guide">
      <div className="guide-hero">
        <span className="guide-hero__logo">
          <ShieldIcon size={32} strokeWidth={1.7} />
        </span>
        {/* 标题不点具体报名类型、也不带年份：可报组合由 `/api/h5/enroll-options` 下发，
            2026-10-31 公共营养师按等级拆为三级/四级后已成三个类型；年份同样是
            每年都要改的常量。两样都写死在文案里，只会让这行字年复一年地过期。 */}
        <h1 className="guide-hero__title">招生报名</h1>
        <p className="guide-hero__desc">报名与学员服务系统</p>
      </div>

      <section className="guide-card">
        <h2 className="guide-card__title">请先关注公众号再报名</h2>
        <p className="guide-card__desc">
          报名入口仅对关注用户开放，关注后从菜单「学员服务」进入即可
        </p>
        {showQr ? (
          <div className="guide-qr">
            <div className="guide-qr__frame">
              <QrArtwork onBroken={handleQrBroken} />
            </div>
            <span className="guide-qr__caption">长按识别二维码关注公众号</span>
          </div>
        ) : (
          /**
           * 二维码不可用（加载失败）：不画空框，改文字引导。
           * 系统只登记了客服码，拿客服码顶替是语义错误 —— 宁可给可执行的
           * 「搜索关注」动作，也不给一个注定失败的长按识别。
           */
          <div className="guide-qr-fallback">
            <span className="guide-qr-fallback__badge">微信</span>
            <p className="guide-qr-fallback__title">在微信内搜索并关注本机构公众号</p>
            <p className="guide-qr-fallback__text">
              打开微信 → 顶部「搜索」→ 输入公众号名称 → 关注后从菜单「学员服务」进入报名
            </p>
          </div>
        )}
      </section>

      <section className="guide-steps-card">
        <ol className="guide-steps">
          {(showQr ? STEPS_WITH_QR : STEPS_BY_SEARCH).map((text, index) => (
            <li key={text} className="guide-step">
              <span className="guide-step__no">{index + 1}</span>
              <span className="guide-step__text">{text}</span>
            </li>
          ))}
        </ol>
      </section>

      <Notice tone="warning">链接分享给他人无效，每人需自行关注后报名</Notice>

      {authError ? <Notice tone="warning">{authErrorText(authError)}</Notice> : null}

      {publicEntrances.length > 0 ? (
        <section className="list-card">
          <div className="list-card__head">无需登录即可查看</div>
          {publicEntrances.map((item) => (
            <ListRow key={item.code} label={item.name} onClick={() => navigate(item.route)} />
          ))}
        </section>
      ) : null}

      {authMode === 'mock' ? (
        <div className="guide-mode-hint">测试模式：未配置公众号凭据，授权为本地模拟</div>
      ) : null}
    </PageShell>
  );
}
